import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronLeft, Check } from "lucide-react";

import SiteLayout from "@/components/SiteLayout";
import Icon from "@/components/Icon";
import PhaseStepper from "@/components/PhaseStepper";
import CountUp from "@/components/CountUp";
import { SERVICES, ENGAGEMENT_MODELS, HETS_PHASES } from "@/data/evara";

const EASE = [0.4, 0, 0.2, 1];

function Reveal({ delay = 0, y = 20, children, className = "", ...rest }) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: EASE, delay: prefersReducedMotion ? 0 : delay }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* --------------- Services HUB view --------------- */
function ServicesHub({ onSelect }) {
  const prefersReducedMotion = useReducedMotion();
  const regular = SERVICES.filter((s) => !s.flagship);
  const flagship = SERVICES.find((s) => s.flagship);

  return (
    <>
      <section className="section-pad bg-sage-gradient" data-testid="services-hero">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="eyebrow">Services</div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
              Seven practices. Modular by design.<br />
              <em className="not-italic text-[var(--evara-primary)] font-medium">Outcomes by default.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg text-[var(--evara-text-2)] max-w-3xl leading-relaxed">
              Engage one practice for a focused problem, or combine several under our six-step framework for end-to-end transformation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Flagship first */}
      {flagship && (
        <section className="section-pad pt-16 pb-4" data-testid="services-flagship-card">
          <div className="max-w-6xl mx-auto">
            <motion.button
              type="button"
              onClick={() => onSelect(flagship.key)}
              className="w-full text-left rounded-3xl border-2 border-[var(--evara-primary)] bg-white p-6 md:p-10 relative overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--evara-primary)]"
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, ease: EASE }}
              whileHover={prefersReducedMotion ? {} : { y: -4 }}
              data-testid={`service-hub-card-${flagship.key}`}
            >
              <span className="absolute top-6 right-6 inline-flex items-center rounded-full bg-[var(--evara-primary)] text-white px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold">
                Flagship diagnostic
              </span>
              <div className="grid md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-1">
                  <div className="w-14 h-14 rounded-2xl bg-[var(--evara-primary)] text-white flex items-center justify-center">
                    <Icon name={flagship.icon} size={26} />
                  </div>
                </div>
                <div className="md:col-span-8">
                  <h2 className="font-serif text-2xl md:text-4xl text-[var(--evara-primary)] leading-tight">
                    {flagship.title}
                  </h2>
                  <p className="mt-3 text-[var(--evara-text-2)] leading-relaxed max-w-2xl">
                    {flagship.blurb}
                  </p>
                </div>
                <div className="md:col-span-3 md:text-right">
                  <span className="btn-primary inline-flex" data-testid={`service-hub-cta-${flagship.key}`}>
                    Explore assessment <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            </motion.button>
          </div>
        </section>
      )}

      {/* Regular practice cards */}
      <section className="section-pad pt-8" data-testid="services-grid">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow mb-6">The other six practices</div>
          <div className="grid md:grid-cols-2 gap-5">
            {regular.map((s, i) => {
              return (
                <motion.button
                  key={s.key}
                  type="button"
                  onClick={() => onSelect(s.key)}
                  className="text-left rounded-2xl border border-[var(--evara-border)] bg-white p-7 md:p-8 hover:border-[var(--evara-border-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--evara-primary)] group"
                  initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.45, ease: EASE, delay: i * 0.08 }}
                  whileHover={prefersReducedMotion ? {} : { y: -4 }}
                  data-testid={`service-hub-card-${s.key}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                      <Icon name={s.icon} size={20} />
                    </span>
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl mt-5 text-[var(--evara-primary)] leading-snug">
                    {s.title}
                  </h3>
                  <p className="text-sm mt-3 text-[var(--evara-text-2)] leading-relaxed">
                    {s.blurb}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-[var(--evara-primary)] group-hover:gap-3 transition-all">
                    Explore practice <ArrowRight size={14} />
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Engagement models */}
      <section className="section-pad bg-soft-sage" data-testid="services-engagement">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="eyebrow">Engagement models</div>
            <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
              Pick the depth <em className="not-italic text-[var(--evara-primary)] font-medium">that fits.</em>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {ENGAGEMENT_MODELS.map((e, i) => (
              <motion.div
                key={e.title}
                className="rounded-2xl border border-[var(--evara-border)] bg-white p-6"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, ease: EASE, delay: i * 0.06 }}
              >
                <div className="font-serif text-xl text-[var(--evara-primary)]">{e.title}</div>
                <p className="mt-2 text-sm text-[var(--evara-text-2)] leading-relaxed">{e.body}</p>
              </motion.div>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-12">
              <Link to="/book" className="btn-primary" data-testid="services-book-btn">
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

/* --------------- Services FOCUSED (single practice) view --------------- */
function ServicePractice({ service }) {
  const prefersReducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const [activePhase, setActivePhase] = React.useState(null); // 0-based framework idx for tooltip
  const phaseSet = new Set(service.frameworkPhases || []);

  return (
    <>
      {/* 1. HERO + OVERVIEW */}
      <section className="section-pad bg-sage-gradient" data-testid="service-hero">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm font-bold text-[var(--evara-primary)] hover:underline"
              data-testid="services-view-all-link"
            >
              <ChevronLeft size={16} /> View all practices
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-5 flex items-center gap-2 flex-wrap">
              {service.flagship && (
                <span className="inline-flex items-center rounded-full bg-[var(--evara-primary)] text-white px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] font-semibold">
                  Flagship diagnostic
                </span>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
              <em className="not-italic text-[var(--evara-primary)] font-medium">{service.title}</em>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg text-[var(--evara-text-2)] leading-relaxed max-w-3xl italic">
              {service.blurb}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad pt-14" data-testid="service-overview">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <Reveal>
              <div className="eyebrow">Overview</div>
              <p className="mt-4 text-[var(--evara-text-2)] leading-relaxed">{service.overview}</p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-[var(--evara-border)] bg-[var(--evara-bg-mint)]/70 p-6">
                <div className="eyebrow mb-2">Who it&apos;s for</div>
                <p className="text-sm text-[var(--evara-text-2)] leading-relaxed">
                  {service.whoItsFor}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. DELIVERABLES */}
      <section className="section-pad pt-14" data-testid="service-deliverables">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="eyebrow">What you get</div>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight text-[var(--evara-primary)]">
              Deliverables from this engagement
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            {service.deliverables.map((d, i) => (
              <motion.div
                key={d}
                className="flex items-start gap-4 rounded-2xl border border-[var(--evara-border)] bg-white p-5"
                initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, ease: EASE, delay: i * 0.07 }}
                whileHover={prefersReducedMotion ? {} : { y: -3 }}
                data-testid={`service-deliverable-${service.key}-${i}`}
              >
                <span className="shrink-0 w-9 h-9 rounded-lg bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                  <Check size={16} />
                </span>
                <span className="text-sm md:text-base text-[var(--evara-text)] leading-relaxed pt-0.5">
                  {d}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. OUTCOMES with count-ups + sample dashboard */}
      <section className="section-pad pt-14 bg-soft-sage" data-testid="service-outcomes">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="eyebrow">Outcomes you can expect</div>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
              Signals we typically move,{" "}
              <em className="not-italic text-[var(--evara-primary)] font-medium">measurably.</em>
            </h2>
          </Reveal>

          {/* Stat cards with count-up */}
          {service.stats && (
            <div className="grid sm:grid-cols-3 gap-5 mt-10">
              {service.stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  className="rounded-2xl border border-[var(--evara-border)] bg-white p-6"
                  initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, ease: EASE, delay: i * 0.08 }}
                  data-testid={`service-stat-${service.key}-${i}`}
                >
                  <CountUp
                    value={s.value}
                    prefix={s.prefix || ""}
                    suffix={s.suffix || ""}
                    className="font-serif text-4xl md:text-5xl text-[var(--evara-primary)] leading-none"
                  />
                  <div className="mt-4 h-[1px] w-10 bg-[var(--evara-mint)]" />
                  <div className="mt-3 text-sm text-[var(--evara-text-2)] leading-relaxed">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Full outcomes list */}
          <ul className="mt-10 grid md:grid-cols-2 gap-y-3 gap-x-8">
            {service.outcomes.map((o, i) => (
              <motion.li
                key={o}
                className="flex items-start gap-3 text-sm"
                initial={prefersReducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, ease: EASE, delay: i * 0.05 }}
                data-testid={`service-outcome-${service.key}-${i}`}
              >
                <span className="mt-1.5 w-2 h-2 rounded-full bg-[var(--evara-primary)] shrink-0" />
                <span className="leading-relaxed">{o}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* 4. FRAMEWORK MINI-STEPPER */}
      <section className="section-pad pt-14" data-testid="service-framework-linkout">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl border border-[var(--evara-border)] bg-white p-6 md:p-10">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <Reveal>
                  <div className="eyebrow">Part of our 6-step framework</div>
                  <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[var(--evara-primary)] leading-tight">
                    This practice draws on{" "}
                    {service.frameworkPhases?.length || HETS_PHASES.length} of the 6 phases.
                  </h3>
                  <p className="mt-3 text-sm text-[var(--evara-text-2)] leading-relaxed max-w-2xl">
                    Hover a highlighted phase to see how it applies to {service.title}. Click any node to open that phase in the framework.
                  </p>
                </Reveal>
              </div>
              <Reveal delay={0.1}>
                <Link
                  to="/framework"
                  className="btn-secondary"
                  data-testid={`service-framework-link-${service.key}`}
                >
                  See full framework <ArrowRight size={16} />
                </Link>
              </Reveal>
            </div>

            <div className="mt-10">
              <PhaseStepper
                variant="full"
                highlightIndices={service.frameworkPhases || [1, 2, 3, 4, 5, 6]}
                showTaglineOnHover={false}
                onSelect={(idx0) => navigate(`/framework?phase=${idx0 + 1}`)}
                onNodeHover={(idx0) => setActivePhase(idx0)}
              />
            </div>

            {/* Tooltip: process body for active phase within this service */}
            <div className="mt-8 min-h-[68px]">
              {activePhase !== null && activePhase >= 0 && phaseSet.has(activePhase + 1) ? (
                <motion.div
                  key={activePhase}
                  className="rounded-2xl bg-[var(--evara-bg-mint)]/70 border border-[rgba(23,105,74,0.15)] p-5"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: EASE }}
                >
                  <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[var(--evara-primary)]">
                    Phase {HETS_PHASES[activePhase].num} · {HETS_PHASES[activePhase].title} for {service.title}
                  </div>
                  <p className="mt-2 text-sm text-[var(--evara-text-2)] leading-relaxed">
                    {service.process.find((p) => p.phase === HETS_PHASES[activePhase].title)?.body ||
                      HETS_PHASES[activePhase].summary}
                  </p>
                </motion.div>
              ) : (
                <div className="text-xs text-[var(--evara-muted)] italic px-1">
                  Hover any highlighted phase above to see how it maps to this practice.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad pt-12" data-testid="service-cta">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[var(--evara-border)] bg-white p-6 md:p-8">
            <div>
              <div className="eyebrow">Ready to explore this?</div>
              <div className="font-serif text-xl md:text-2xl mt-2 text-[var(--evara-primary)]">
                Book a consultation and we&apos;ll walk you through the fit.
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {service.key === "assessment" && (
                <Link
                  to="/operational-assessment"
                  className="btn-secondary"
                  data-testid="service-panel-oa-link"
                >
                  Full assessment page <ArrowRight size={16} />
                </Link>
              )}
              <Link
                to="/book"
                className="btn-primary"
                data-testid={`service-panel-cta-${service.key}`}
              >
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* --------------- Services page shell --------------- */
export default function Services() {
  const [params, setParams] = useSearchParams();
  const paramKey = params.get("practice");
  const service = SERVICES.find((s) => s.key === paramKey);
  const focused = Boolean(service);

  const goTo = (key) => {
    setParams({ practice: key });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SiteLayout>
      {focused ? (
        <ServicePractice service={service} />
      ) : (
        <ServicesHub onSelect={goTo} />
      )}
    </SiteLayout>
  );
}
